// src/components/FbDisclaimerBuilder.js
// Modal that assembles a compliant Facebook ad "Special Disclaimer" from
// toggleable offer blocks ($0 Down, No Payments 90 Days, 0% APR, Payment
// Example). Payment math and per-$1,000 rates are computed automatically.
// The result is inserted into the client's Special Disclaimer field, which
// then flows through the existing image + Bitly generation unchanged.

import React, { useState, useMemo } from 'react';
import {
  buildFbSpecialDisclaimer,
  calcPaymentFromDue,
  calcPerThousand,
} from '../utils/ftcCompliance';

const overlayStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.5)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};

const modalStyle = {
  background: 'white', borderRadius: '10px', width: '90%', maxWidth: '760px',
  maxHeight: '90vh', overflowY: 'auto', padding: '20px',
};

const blockStyle = (enabled) => ({
  border: `1px solid ${enabled ? '#10b981' : '#e5e7eb'}`,
  borderRadius: '8px', padding: '10px 12px', marginBottom: '10px',
  background: enabled ? '#f0fdf4' : '#f9fafb',
});

const inputStyle = {
  width: '100%', padding: '6px 8px', border: '1px solid #d1d5db',
  borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box',
};

const fieldCol = { display: 'flex', flexDirection: 'column', gap: '2px', flex: '1 1 120px', minWidth: '110px' };
const fieldLabel = { fontSize: '11px', fontWeight: '600', color: '#6b7280' };
const rowStyle = { display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' };

function FbDisclaimerBuilder({ open, onClose, client, onInsert }) {
  const [config, setConfig] = useState({
    fees: {
      preDeliveryFee: client?.preDeliveryFee || '1298',
      privateTagFee: client?.privateTagFee || '189',
      electronicFilingFee: client?.electronicFilingFee || '598',
    },
    endDate: '',
    zeroDown: { enabled: true },
    noPayments90: { enabled: false },
    zeroApr: { enabled: false, year: '', make: client?.manufacturer || '', model: '', trim: '', vin: '', months: 72, financeCompany: '' },
    paymentExample: { enabled: false, condition: 'Pre-Owned', year: '', make: '', model: '', trim: '', stockNumber: '', salePrice: '', apr: 5.9, months: 84, dueAtSigning: '0' },
  });

  const preview = useMemo(() => buildFbSpecialDisclaimer(config), [config]);

  const warnings = useMemo(() => {
    const w = [];
    if (!config.endDate) w.push('Offer end date is required (checklist: expiration dates must be real).');
    if (config.zeroApr.enabled && !config.zeroApr.vin) w.push('0% APR offer: VIN missing (checklist: vehicle identification).');
    if (config.zeroApr.enabled && (!config.zeroApr.year || !config.zeroApr.model)) w.push('0% APR offer: year/make/model incomplete.');
    if (config.paymentExample.enabled && !config.paymentExample.stockNumber) w.push('Payment example: Stock# missing (checklist: vehicle identification).');
    if (config.paymentExample.enabled && !config.paymentExample.salePrice) w.push('Payment example: sale price missing - payment cannot be verified.');
    const f = config.fees;
    if (!parseFloat(f.preDeliveryFee) && !parseFloat(f.privateTagFee) && !parseFloat(f.electronicFilingFee)) {
      w.push('No fee amounts entered - the fee sentence will be generated without dollar amounts.');
    }
    return w;
  }, [config]);

  if (!open) return null;

  const set = (path, value) => {
    setConfig((prev) => {
      const next = { ...prev };
      if (path.length === 1) next[path[0]] = value;
      else next[path[0]] = { ...prev[path[0]], [path[1]]: value };
      return next;
    });
  };

  const pmt = config.paymentExample;
  const computedPayment = calcPaymentFromDue(pmt.salePrice, pmt.dueAtSigning, pmt.apr, parseInt(pmt.months, 10) || 0);
  const computedPerK = calcPerThousand(pmt.apr, parseInt(pmt.months, 10) || 0);
  const zeroAprPerK = config.zeroApr.months ? (1000 / parseInt(config.zeroApr.months, 10)).toFixed(2) : '';

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0 }}>Facebook Disclaimer Builder</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>×</button>
        </div>

        {/* Fees + end date */}
        <div style={{ ...blockStyle(true), background: '#eff6ff', borderColor: '#93c5fd' }}>
          <strong style={{ fontSize: '13px' }}>Dealer Fees &amp; Offer End Date</strong>
          <div style={rowStyle}>
            <div style={fieldCol}>
              <label style={fieldLabel}>Pre-Delivery ($)</label>
              <input style={inputStyle} type="number" value={config.fees.preDeliveryFee} onChange={(e) => set(['fees', 'preDeliveryFee'], e.target.value)} />
            </div>
            <div style={fieldCol}>
              <label style={fieldLabel}>Private Tag ($)</label>
              <input style={inputStyle} type="number" value={config.fees.privateTagFee} onChange={(e) => set(['fees', 'privateTagFee'], e.target.value)} />
            </div>
            <div style={fieldCol}>
              <label style={fieldLabel}>E-Filing ($)</label>
              <input style={inputStyle} type="number" value={config.fees.electronicFilingFee} onChange={(e) => set(['fees', 'electronicFilingFee'], e.target.value)} />
            </div>
            <div style={fieldCol}>
              <label style={fieldLabel}>All Offers End</label>
              <input style={inputStyle} type="date" value={config.endDate} onChange={(e) => set(['endDate'], e.target.value)} />
            </div>
          </div>
        </div>

        {/* $0 Down */}
        <div style={blockStyle(config.zeroDown.enabled)}>
          <label style={{ display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
            <input type="checkbox" checked={config.zeroDown.enabled} onChange={(e) => set(['zeroDown', 'enabled'], e.target.checked)} />
            $0 Down Payment offer
          </label>
        </div>

        {/* No payments 90 days */}
        <div style={blockStyle(config.noPayments90.enabled)}>
          <label style={{ display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
            <input type="checkbox" checked={config.noPayments90.enabled} onChange={(e) => set(['noPayments90', 'enabled'], e.target.checked)} />
            No Payments for 90 Days offer
          </label>
        </div>

        {/* 0% APR */}
        <div style={blockStyle(config.zeroApr.enabled)}>
          <label style={{ display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
            <input type="checkbox" checked={config.zeroApr.enabled} onChange={(e) => set(['zeroApr', 'enabled'], e.target.checked)} />
            0% APR offer {config.zeroApr.enabled && zeroAprPerK && (
              <span style={{ fontWeight: 400, fontSize: '12px', color: '#059669' }}>
                → ${zeroAprPerK} per month per $1,000 financed
              </span>
            )}
          </label>
          {config.zeroApr.enabled && (
            <div style={rowStyle}>
              <div style={fieldCol}><label style={fieldLabel}>Year</label>
                <input style={inputStyle} value={config.zeroApr.year} onChange={(e) => set(['zeroApr', 'year'], e.target.value)} placeholder="2026" /></div>
              <div style={fieldCol}><label style={fieldLabel}>Make</label>
                <input style={inputStyle} value={config.zeroApr.make} onChange={(e) => set(['zeroApr', 'make'], e.target.value)} placeholder="Mazda" /></div>
              <div style={fieldCol}><label style={fieldLabel}>Model</label>
                <input style={inputStyle} value={config.zeroApr.model} onChange={(e) => set(['zeroApr', 'model'], e.target.value)} placeholder="CX-70 PHEV" /></div>
              <div style={fieldCol}><label style={fieldLabel}>Trim (optional)</label>
                <input style={inputStyle} value={config.zeroApr.trim} onChange={(e) => set(['zeroApr', 'trim'], e.target.value)} /></div>
              <div style={fieldCol}><label style={fieldLabel}>VIN</label>
                <input style={inputStyle} value={config.zeroApr.vin} onChange={(e) => set(['zeroApr', 'vin'], e.target.value)} placeholder="Full VIN" /></div>
              <div style={fieldCol}><label style={fieldLabel}>Term (months)</label>
                <input style={inputStyle} type="number" value={config.zeroApr.months} onChange={(e) => set(['zeroApr', 'months'], e.target.value)} placeholder="72" /></div>
              <div style={fieldCol}><label style={fieldLabel}>Finance Company (optional)</label>
                <input style={inputStyle} value={config.zeroApr.financeCompany} onChange={(e) => set(['zeroApr', 'financeCompany'], e.target.value)} placeholder="Defaults to <Make> Financial Services" /></div>
            </div>
          )}
        </div>

        {/* Payment example */}
        <div style={blockStyle(config.paymentExample.enabled)}>
          <label style={{ display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
            <input type="checkbox" checked={config.paymentExample.enabled} onChange={(e) => set(['paymentExample', 'enabled'], e.target.checked)} />
            "Payments as low as" example vehicle {config.paymentExample.enabled && !!computedPayment && (
              <span style={{ fontWeight: 400, fontSize: '12px', color: '#059669' }}>
                → ${computedPayment}/mo @ ${computedPerK.toFixed(2)} per $1,000
              </span>
            )}
          </label>
          {config.paymentExample.enabled && (
            <div style={rowStyle}>
              <div style={fieldCol}><label style={fieldLabel}>Condition</label>
                <select style={inputStyle} value={pmt.condition} onChange={(e) => set(['paymentExample', 'condition'], e.target.value)}>
                  <option>Pre-Owned</option>
                  <option>New</option>
                  <option>Certified Pre-Owned</option>
                </select></div>
              <div style={fieldCol}><label style={fieldLabel}>Year</label>
                <input style={inputStyle} value={pmt.year} onChange={(e) => set(['paymentExample', 'year'], e.target.value)} placeholder="2019" /></div>
              <div style={fieldCol}><label style={fieldLabel}>Make</label>
                <input style={inputStyle} value={pmt.make} onChange={(e) => set(['paymentExample', 'make'], e.target.value)} placeholder="Dodge" /></div>
              <div style={fieldCol}><label style={fieldLabel}>Model</label>
                <input style={inputStyle} value={pmt.model} onChange={(e) => set(['paymentExample', 'model'], e.target.value)} placeholder="Grand Caravan" /></div>
              <div style={fieldCol}><label style={fieldLabel}>Trim (optional)</label>
                <input style={inputStyle} value={pmt.trim} onChange={(e) => set(['paymentExample', 'trim'], e.target.value)} placeholder="SXT" /></div>
              <div style={fieldCol}><label style={fieldLabel}>Stock #</label>
                <input style={inputStyle} value={pmt.stockNumber} onChange={(e) => set(['paymentExample', 'stockNumber'], e.target.value)} placeholder="171773A" /></div>
              <div style={fieldCol}><label style={fieldLabel}>Sale Price ($)</label>
                <input style={inputStyle} type="number" value={pmt.salePrice} onChange={(e) => set(['paymentExample', 'salePrice'], e.target.value)} placeholder="10515" /></div>
              <div style={fieldCol}><label style={fieldLabel}>APR (%)</label>
                <input style={inputStyle} type="number" step="0.1" value={pmt.apr} onChange={(e) => set(['paymentExample', 'apr'], e.target.value)} /></div>
              <div style={fieldCol}><label style={fieldLabel}>Term (months)</label>
                <input style={inputStyle} type="number" value={pmt.months} onChange={(e) => set(['paymentExample', 'months'], e.target.value)} /></div>
              <div style={fieldCol}><label style={fieldLabel}>Due at Signing ($)</label>
                <input style={inputStyle} type="number" value={pmt.dueAtSigning} onChange={(e) => set(['paymentExample', 'dueAtSigning'], e.target.value)} placeholder="0" /></div>
            </div>
          )}
        </div>

        {/* Warnings */}
        {warnings.length > 0 && (
          <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '6px', padding: '8px 12px', fontSize: '12px', color: '#92400e', marginBottom: '10px' }}>
            {warnings.map((w, i) => <div key={i}>⚠️ {w}</div>)}
          </div>
        )}

        {/* Preview */}
        <div style={{ marginBottom: '12px' }}>
          <strong style={{ fontSize: '13px' }}>Preview</strong>
          <div style={{
            whiteSpace: 'pre-wrap', fontFamily: 'Monaco, Consolas, monospace', fontSize: '11px',
            background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px',
            padding: '10px', maxHeight: '200px', overflowY: 'auto', marginTop: '4px',
          }}>
            {preview || <em>Enable at least one offer block above.</em>}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: '#6b7280', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
            Cancel
          </button>
          <button
            onClick={() => { onInsert(preview); onClose(); }}
            disabled={!preview}
            style={{ padding: '8px 16px', background: preview ? '#28a745' : '#d1d5db', color: 'white', border: 'none', borderRadius: '6px', cursor: preview ? 'pointer' : 'not-allowed', fontSize: '13px', fontWeight: 600 }}
          >
            Insert into Special Disclaimer
          </button>
        </div>
      </div>
    </div>
  );
}

export default FbDisclaimerBuilder;